import type { MarketplaceFulfillmentStatus } from "@nexus/contracts";
import {
  Button,
  DateRangePicker,
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Icon,
  Input,
  Toggle,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  cn,
  type DateRange,
} from "@nexus/ui";
import { useId, useMemo, type ComponentProps, type Ref } from "react";
import { useTranslation } from "react-i18next";

import { parseYmdToLocalDate, toLocalYmd } from "../../lib/order-deadline";
import {
  clearOrdersListFilters,
  countActiveFilters,
  NATIVE_FULFILLMENT_OPTIONS,
  ORDERS_GROUP_BY_OPTIONS,
  ORDERS_SORT_BY_OPTIONS,
  type OrdersGroupBy,
  type OrdersListQuery,
  type OrdersSortBy,
  type VendorOption,
} from "../../lib/orders-list-model";
import { OrderLineTypeFilterMenu } from "./OrderLineTypeFilterMenu";
import {
  STATUS_FILTER_OPTION_ITEM_CLASS,
  StatusOptionContent,
} from "./StatusOptionContent";

interface OrdersToolbarProps {
  query: OrdersListQuery;
  vendors: VendorOption[];
  attentionCount: number;
  shownCount: number;
  totalCount: number;
  /** History tab has nothing overdue to chase — hide the attention toggle. */
  showAttentionFilter: boolean;
  onExport?: () => void;
  exportDisabled?: boolean;
  exportPending?: boolean;
  onChange: (next: OrdersListQuery) => void;
  searchInputRef?: Ref<HTMLInputElement>;
}

const GROUP_BY_LABEL_KEY: Record<OrdersGroupBy, string> = {
  none: "marketplace.orders.list.groupNone",
  vendor: "marketplace.orders.list.groupVendor",
  status: "marketplace.orders.list.groupStatus",
  deadline: "marketplace.orders.list.groupDeadline",
};

const SORT_BY_LABEL_KEY: Record<OrdersSortBy, string> = {
  deadline: "marketplace.orders.list.sortDeadline",
  createdAt: "marketplace.orders.list.sortCreatedAt",
  vendor: "marketplace.orders.list.sortVendor",
  scripts: "marketplace.orders.list.columns.scripts",
  status: "marketplace.orders.list.sortStatus",
};

/** Spreads the Radix trigger props it receives through `asChild` onto the button. */
function FilterTrigger({
  label,
  active,
  count,
  className,
  ...triggerProps
}: ComponentProps<typeof Button> & {
  label: string;
  active: boolean;
  count?: number;
}) {
  return (
    <Button
      type="button"
      variant="outline"
      size="sm"
      iconEnd="chevron-down"
      className={cn(
        "h-8 text-xs",
        active && "border-primary/40 bg-primary-subtle/40 text-primary",
        className,
      )}
      {...triggerProps}
    >
      {label}
      {count ? <span className="tabular-nums">{count}</span> : null}
    </Button>
  );
}

export function OrdersToolbar({
  query,
  vendors,
  attentionCount,
  shownCount,
  totalCount,
  showAttentionFilter,
  onExport,
  exportDisabled = false,
  exportPending = false,
  onChange,
  searchInputRef,
}: OrdersToolbarProps) {
  const { t } = useTranslation();
  const searchId = useId();
  const activeFilters = countActiveFilters(query);
  const showVendorFilter = vendors.length > 1;
  const dueRange = useMemo<DateRange | undefined>(() => {
    const from = query.dueFrom ? parseYmdToLocalDate(query.dueFrom) : null;
    const to = query.dueTo ? parseYmdToLocalDate(query.dueTo) : null;
    if (!from && !to) return undefined;
    return { from: from ?? undefined, to: to ?? undefined };
  }, [query.dueFrom, query.dueTo]);

  const toggleStatus = (
    status: MarketplaceFulfillmentStatus,
    checked: boolean,
  ) => {
    const next = checked
      ? [...query.statuses, status]
      : query.statuses.filter((current) => current !== status);
    onChange({ ...query, statuses: next });
  };

  const toggleVendor = (vendorId: string, checked: boolean) => {
    const next = checked
      ? [...query.vendorIds, vendorId]
      : query.vendorIds.filter((current) => current !== vendorId);
    onChange({ ...query, vendorIds: next });
  };

  return (
    <div
      className="flex flex-col gap-2 lg:flex-row lg:items-start"
      role="toolbar"
      aria-label={t("marketplace.orders.list.toolbarLabel")}
    >
      <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2">
        <div className="w-full sm:w-64">
          <label htmlFor={searchId} className="sr-only">
            {t("marketplace.orders.list.searchLabel")}
          </label>
          <Input
            id={searchId}
            ref={searchInputRef}
            type="search"
            iconStart="search"
            value={query.search}
            placeholder={t("marketplace.orders.list.searchPlaceholder")}
            onChange={(event) =>
              onChange({ ...query, search: event.target.value })
            }
          />
        </div>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <FilterTrigger
              label={t("marketplace.orders.list.statusFilter")}
              active={query.statuses.length > 0}
              count={query.statuses.length}
            />
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="min-w-72">
            <DropdownMenuLabel className="text-xs text-muted-foreground">
              {t("marketplace.orders.list.statusFilter")}
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            {NATIVE_FULFILLMENT_OPTIONS.map((status) => (
              <DropdownMenuCheckboxItem
                key={status}
                className={STATUS_FILTER_OPTION_ITEM_CLASS}
                checked={query.statuses.includes(status)}
                onSelect={(event) => event.preventDefault()}
                onCheckedChange={(checked) =>
                  toggleStatus(status, checked === true)
                }
              >
                <StatusOptionContent status={status} />
              </DropdownMenuCheckboxItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>

        {showVendorFilter ? (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <FilterTrigger
                label={t("marketplace.orders.list.vendorFilter")}
                active={query.vendorIds.length > 0}
                count={query.vendorIds.length}
              />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="min-w-56">
              <DropdownMenuLabel className="text-xs text-muted-foreground">
                {t("marketplace.orders.list.vendorFilter")}
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              {vendors.map((vendor) => (
                <DropdownMenuCheckboxItem
                  key={vendor.id}
                  className="text-xs"
                  checked={query.vendorIds.includes(vendor.id)}
                  onSelect={(event) => event.preventDefault()}
                  onCheckedChange={(checked) =>
                    toggleVendor(vendor.id, checked === true)
                  }
                >
                  {vendor.name}
                </DropdownMenuCheckboxItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        ) : null}

        <OrderLineTypeFilterMenu
          value={query.lineType}
          onChange={(lineType) => onChange({ ...query, lineType })}
          renderTrigger={({ label, isActive }) => (
            <FilterTrigger label={label} active={isActive} />
          )}
        />

        {showAttentionFilter ? (
          <Tooltip>
            <TooltipTrigger asChild>
              <Toggle
                variant="outline"
                size="sm"
                pressed={query.attentionOnly}
                aria-label={t("marketplace.orders.list.attentionFilter")}
                className={cn(
                  "h-8 gap-1.5 px-3 text-xs",
                  query.attentionOnly &&
                    "border-error/40 bg-error-subtle/60 text-error-subtle-foreground",
                )}
                onPressedChange={(pressed) =>
                  onChange({ ...query, attentionOnly: pressed })
                }
              >
                <Icon
                  name="alert-circle"
                  size="sm"
                  color="inherit"
                  aria-hidden
                />
                {t("marketplace.orders.list.attentionFilter")}
                {attentionCount > 0 ? (
                  <span className="tabular-nums text-muted-foreground">
                    {attentionCount}
                  </span>
                ) : null}
              </Toggle>
            </TooltipTrigger>
            <TooltipContent>
              {t("marketplace.orders.list.attentionFilterHint")}
            </TooltipContent>
          </Tooltip>
        ) : null}

        <Toggle
          variant="outline"
          size="sm"
          pressed={query.overdueOnly}
          aria-label={t("marketplace.orders.list.overdueOnly")}
          className={cn(
            "h-8 gap-1.5 px-3 text-xs",
            query.overdueOnly &&
              "border-error/40 bg-error-subtle/60 text-error-subtle-foreground",
          )}
          onPressedChange={(pressed) =>
            onChange({ ...query, overdueOnly: pressed })
          }
        >
          <Icon name="alert-circle" size="sm" color="inherit" aria-hidden />
          {t("marketplace.orders.list.overdueOnly")}
        </Toggle>

        <DateRangePicker
          value={dueRange}
          onChange={(range) =>
            onChange({
              ...query,
              dueFrom: range?.from ? toLocalYmd(range.from) : "",
              dueTo: range?.to ? toLocalYmd(range.to) : "",
            })
          }
          numberOfMonths={2}
          independentMonths={false}
          align="start"
          weekStartsOn={1}
          triggerClassName={cn(
            "h-8 text-xs",
            dueRange && "border-primary/40 bg-primary-subtle/40 text-primary",
          )}
          labels={{
            placeholder: t("marketplace.orders.list.dueFilter"),
            triggerAria: t("marketplace.orders.list.dueRangeAria"),
            clear: t("marketplace.orders.list.clearDueRange"),
            done: t("marketplace.orders.list.applyDueRange"),
            apply: t("marketplace.orders.list.applyDueRange"),
            from: t("marketplace.orders.list.dueFrom"),
            to: t("marketplace.orders.list.dueTo"),
          }}
        />

        {activeFilters > 0 ? (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            iconStart="x"
            className="h-8 text-xs text-muted-foreground"
            onClick={() => onChange(clearOrdersListFilters(query))}
          >
            {t("marketplace.orders.list.clearFilters")}
          </Button>
        ) : null}
      </div>

      <div className="flex shrink-0 items-center gap-2 self-end lg:self-auto">
        {onExport ? (
          <Button
            type="button"
            variant="outline"
            size="sm"
            iconStart={exportPending ? undefined : "download"}
            className="h-8 text-xs"
            disabled={exportDisabled || exportPending}
            aria-busy={exportPending || undefined}
            onClick={onExport}
          >
            {exportPending ? (
              <>
                <Icon
                  name="loader"
                  size="sm"
                  color="inherit"
                  className="animate-spin"
                  aria-hidden
                />
                {t("marketplace.orders.list.export.exporting")}
              </>
            ) : (
              t("marketplace.orders.list.export.excel")
            )}
          </Button>
        ) : null}

        <span
          className="hidden text-xs tabular-nums text-muted-foreground sm:inline"
          aria-live="polite"
        >
          {shownCount === totalCount
            ? t("marketplace.orders.list.resultCount", { count: totalCount })
            : t("marketplace.orders.list.resultCountFiltered", {
                shown: shownCount,
                total: totalCount,
              })}
        </span>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              iconStart="rows-3"
              iconEnd="chevron-down"
              className={cn(
                "h-8 text-xs",
                query.groupBy !== "none" && "text-primary",
              )}
              aria-label={t("marketplace.orders.list.groupByLabel")}
            >
              {query.groupBy === "none"
                ? t("marketplace.orders.list.groupBy")
                : t(GROUP_BY_LABEL_KEY[query.groupBy])}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="min-w-44">
            <DropdownMenuLabel className="text-xs text-muted-foreground">
              {t("marketplace.orders.list.groupByLabel")}
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuRadioGroup
              value={query.groupBy}
              onValueChange={(value) =>
                onChange({ ...query, groupBy: value as OrdersGroupBy })
              }
            >
              {ORDERS_GROUP_BY_OPTIONS.map((option) => (
                <DropdownMenuRadioItem
                  key={option}
                  value={option}
                  className="text-xs"
                >
                  {t(GROUP_BY_LABEL_KEY[option])}
                </DropdownMenuRadioItem>
              ))}
            </DropdownMenuRadioGroup>
          </DropdownMenuContent>
        </DropdownMenu>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              iconStart="sliders-horizontal"
              iconEnd="chevron-down"
              className="h-8 text-xs"
              aria-label={t("marketplace.orders.list.sortByLabel")}
            >
              {t(SORT_BY_LABEL_KEY[query.sortBy])}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="min-w-44">
            <DropdownMenuLabel className="text-xs text-muted-foreground">
              {t("marketplace.orders.list.sortByLabel")}
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuRadioGroup
              value={query.sortBy}
              onValueChange={(value) => {
                const sortBy = value as OrdersSortBy;
                onChange({
                  ...query,
                  sortBy,
                  sortDirection: sortBy === "createdAt" ? "desc" : "asc",
                });
              }}
            >
              {ORDERS_SORT_BY_OPTIONS.map((option) => (
                <DropdownMenuRadioItem
                  key={option}
                  value={option}
                  className="text-xs"
                >
                  {t(SORT_BY_LABEL_KEY[option])}
                </DropdownMenuRadioItem>
              ))}
            </DropdownMenuRadioGroup>
          </DropdownMenuContent>
        </DropdownMenu>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              type="button"
              variant="ghost"
              size="icon-sm"
              aria-label={t("marketplace.orders.list.density.label")}
            >
              <Icon
                name={query.density === "compact" ? "rows-4" : "rows-2"}
                size="sm"
                color="inherit"
              />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="min-w-40">
            <DropdownMenuLabel className="text-xs text-muted-foreground">
              {t("marketplace.orders.list.density.label")}
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuRadioGroup
              value={query.density}
              onValueChange={(value) =>
                onChange({
                  ...query,
                  density: value as OrdersListQuery["density"],
                })
              }
            >
              <DropdownMenuRadioItem value="compact" className="text-xs">
                {t("marketplace.orders.list.density.compact")}
              </DropdownMenuRadioItem>
              <DropdownMenuRadioItem value="comfortable" className="text-xs">
                {t("marketplace.orders.list.density.comfortable")}
              </DropdownMenuRadioItem>
            </DropdownMenuRadioGroup>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
}
